/**
 * Equipment type definitions for the ELDRICK gear testing system.
 *
 * Two-tier model:
 *   1. Named gear (DriverHead, IronSet, Shaft, GolfBall) — real products with
 *      calibrated DNA profiles sourced from published launch monitor data.
 *   2. Generic specs (GearSpecs) — user-inputtable parameters for spec-based
 *      comparison testing when exact gear isn't known.
 *
 * Gear DNA: each piece of equipment has internal physics parameters beyond
 * the consumer-facing specs (weight, flex). These model the real differentiators
 * that fitters use to match gear to swing:
 *   - Shaft: kick point, torque, tip stiffness, butt stiffness, length
 *   - Head: CG depth/height, MOI, face flex
 *   - Ball: layer count, dimple drag profile
 */

// ── Shaft DNA ──────────────────────────────────────────────────────────────

export type KickPoint = 'low' | 'mid' | 'mid-high' | 'high';

export interface ShaftProfile {
  /** Kick point — where the shaft bends most during the downswing.
   *  Low = higher launch/spin (more tip deflection at impact).
   *  High = lower launch/spin (stiffer tip, less loft added). */
  kickPoint: KickPoint;

  /** Torque in degrees — resistance to twisting under load.
   *  Range: 2.0° (very stiff) – 7.0° (very flexible).
   *  Lower = less face rotation = tighter dispersion.
   *  Higher = more face rotation = wider offline + spin axis shift. */
  torque: number;

  /** Tip stiffness relative to nominal flex (0.80 – 1.20).
   *  1.0 = standard. <1.0 = softer tip (higher launch/spin).
   *  >1.0 = stiffer tip (lower launch/spin) — "low-spin" profiles.
   *  This differentiates two shafts at the same weight/flex. */
  tipStiffness: number;

  /** Butt stiffness relative to nominal (0.80 – 1.20).
   *  Affects loading timing. Stiffer butt = later release.
   *  Fast tempo + stiff butt = optimal. Slow tempo + stiff butt = under-loaded. */
  buttStiffness: number;
}

// ── Head DNA ───────────────────────────────────────────────────────────────

export interface HeadProfile {
  /** CG depth in mm from face center (0 = face-aligned, 30+ = deep back).
   *  Forward CG: lower spin, less forgiveness.
   *  Deep CG: higher launch, more spin, more forgiving.
   *  Range: 10–40 mm for drivers. */
  cgDepth: number;

  /** CG height in mm from sole.
   *  Low CG: higher launch angle for same loft.
   *  High CG: lower launch, more workable.
   *  Range: 15–25 mm for drivers, 10–20 for irons. */
  cgHeight: number;

  /** MOI in g·cm² — moment of inertia (resistance to twisting on mishits).
   *  Higher = less speed/spin loss off-center.
   *  Range: 4000–5900 for drivers (USGA max ~5900). Irons: 2000–3500. */
  moi: number;

  /** Face flex modifier (0.98 – 1.03).
   *  1.0 = standard CT. >1.0 = "hot" face (AI-designed).
   *  <1.0 = softer feel, slightly less speed (forged). */
  faceFlexMod: number;
}

// ── Ball DNA ───────────────────────────────────────────────────────────────

export interface BallProfile {
  /** Number of layers (2–5).
   *  2-piece: low spin separation — distance balls.
   *  3-piece: moderate spin separation.
   *  4/5-piece: max spin separation — premium tour. */
  layers: number;

  /** Dimple profile affects lift/drag balance.
   *  Penetrating: lower peak, more roll.
   *  High-flight: higher apex, steeper landing. */
  dimpleProfile: 'penetrating' | 'mid' | 'high-flight';
}

// ── Equipment Types (with DNA) ─────────────────────────────────────────────

export interface DriverHead {
  id: string;
  name: string;
  brand: string;
  year: number;
  loft: number;
  ballSpeedMod: number;
  launchMod: number;
  spinMod: number;
  head: HeadProfile;
  /** Optional TrackMan-derived response for how this driver's spin changes with delivery. */
  spinResponse?: {
    speedSlopeRpmPerMph: number;
    attackSlopeRpmPerDeg: number;
    referenceClubSpeed: number;
    referenceAttackAngle: number;
    maxAdjustmentRpm: number;
  };
}

export interface IronSet {
  id: string;
  name: string;
  brand: string;
  year: number;
  category: IronCategory;
  ballSpeedMod: number;
  launchMod: number;
  spinMod: number;
  head: HeadProfile;
}

export interface Shaft {
  id: string;
  name: string;
  brand: string;
  flex: string;
  weight: number;
  length: number;         // inches (driver: 43–46.5, irons vary)
  material: 'graphite' | 'steel';
  launchMod: number;
  spinMod: number;
  profile: ShaftProfile;
}

export interface GolfBall {
  id: string;
  name: string;
  brand: string;
  compression: number;
  cover: BallCover;
  speedMod: number;
  driverSpinMod: number;
  ironSpinMod: number;
  wedgeSpinMod: number;
  ball: BallProfile;
}

export interface EquipmentBag {
  driver: DriverHead;
  irons: IronSet;
  shaft: Shaft;
  ball: GolfBall;
}

export interface LaunchModifiers {
  ballSpeedMod: number;
  launchMod: number;
  spinMod: number;
  /** Dispersion modifier — multiplier on offline spread (lower = tighter). */
  dispersionMod: number;
}

// ── Spec-based gear testing ────────────────────────────────────────────────

export type ShaftFlex = 'L' | 'A' | 'R' | 'S' | 'X';
export type IronCategory = 'blade' | 'players' | 'players-distance' | 'game-improvement';

export type BallCover = 'urethane' | 'surlyn' | 'ionomer';

export type LaunchProfile = 'low' | 'mid-low' | 'mid' | 'mid-high' | 'high';
export type HeadType = 'standard' | 'draw' | 'fade' | 'low-spin' | 'max-forgiveness';
export type WeightSetting = 'standard' | 'draw' | 'fade' | 'back' | 'front';

// ── ELDRICK Fitting Tool Payload ──────────────────────────────────────────

/** Wrapper for physics-estimated values from ELDRICK. */
export interface AssumedValue {
  value: number;
  assumed: boolean;
  basis: string;
}

/** Unwrap an assumed value or plain number (null-safe). */
export function unwrapValue(v: number | AssumedValue | null | undefined): number | null {
  if (v == null) return null;
  if (typeof v === 'number') return v;
  if (v && typeof v === 'object' && 'value' in v) return v.value;
  return null;
}

/** Full payload from POST /api/labs/simulate. */
export interface EldrickPayload {
  /** Optional same-player/session/current-equipment cohort. See docs/paired-comparisons.md. */
  swingSamples?: EldrickPayload['swing'][];
  meta: {
    source?: 'eldrick-core' | 'eldrick-api';
    objective?: 'balanced' | 'distance' | 'accuracy';
    fittingId: string;
    fittingType: string | null;     // 'driver' | 'iron'
    fittingDepth: string | null;    // 'quick_fit' | 'deep_fit'
    generatedAt: string;
  };
  player: {
    handedness: string | null;
    height: { feet: number; inches: number; totalInches: number } | null;
    wristToFloor: number | null;
    weight: number | null;
    handicap: string | number | null;
    goals: string | null;
    experience: string | null;
    feelBalance: string | null;
    controlConfidence: string | null;
    overallSatisfaction: string | null;
  };
  swing: {
    clubheadSpeed: number | null;
    ballSpeed: number | null;
    launchAngle: number | null;
    /** Optional measured horizontal launch direction, degrees right of target. */
    launchDirection?: number | null;
    spinRate: number | null;
    attackAngle: number | null;
    clubPath: number | null;
    faceAngle: number | null;
    carryDistance: number | null;
    landingAngle: number | null;
    peakHeight: number | null;
    peakHeightContext: string | null;
    missPattern: string | null;
    smashFactor: number | null;
    faceToPaths: number | null;
    handedness: string | null;
    dynamicLoft: AssumedValue | null;
    spinAxis: number | AssumedValue | null;
    impactEfficiency: AssumedValue | null;
  };
  swingProfile: {
    tempo: string | null;
    transition: string | null;
    release: string | null;
    strikePattern: string | null;
    dispersionShape: string | null;
  };
  gear: {
    loft: number | null;
    loftLabel: string | null;
    lieAngle: number | null;
    lieAngleLabel: string | null;
    lieAngleOverride: string | null;
    length: number | null;
    lengthLabel: string | null;
    flex: string | null;
    shaftWeight: number | null;
    shaftMaterial: string | null;
    swingWeight: string | null;
    launchProfile: string | null;
    headCategory: string | null;
    headTypeOverride: string | null;
    weightSetting: string | null;
    adapterSetting: string | null;
    effectiveLoft: string | null;
    swingFlawFlag: boolean | null;
    cor: AssumedValue | null;
    moi: AssumedValue | null;
    faceThickness: AssumedValue | null;
  };
  currentSetup: {
    brand: string | null;
    model: string | null;
    loft: number | null;
    length: number | null;
    lengthStep: number | null;
    shaftModel: string | null;
    shaftFlex: string | null;
    shaftWeight: number | null;
    shaftMaterial: string | null;
    swingWeight: number | null;
    ball: string | null;
    ballCompression: AssumedValue | null;
  };
}

// ── Spec-based gear testing (continued) ──────────────────────────────────

export interface GearSpecs {
  loft: number;              // driver loft: 8–12° (default 10.5)
  shaftWeight: number;       // grams: 40–130 (default 65)
  shaftFlex: ShaftFlex;      // default 'S'
  ballCompression: number;   // 30–110 (default 90)
  ballCover: BallCover;      // default 'urethane'
  ironCategory: IronCategory; // default 'players'
  // Detailed fitting parameters
  clubLength: number;        // inches: 43–46.5 for driver (default 45.5)
  swingWeight: string;       // D0–D6 (default 'D3')
  launchProfile: LaunchProfile; // default 'mid'
  headType: HeadType;        // default 'standard'
  weightSetting: WeightSetting; // default 'standard'
}
